import { Component, Vue, Ref, Prop, Watch } from "vue-property-decorator";
import ChildNavigation from "@/components/navigation/child-navigation/child-navigation.vue";
import { Spin, Select, Icon } from "ant-design-vue";
import { TitleListLocale } from "@/locales/localeid";
import { ActionItem, fmtMsg, MessageHelper, NotificationType } from "commonui";
import { GridRef, IWijmoSort } from "@/components/wijmo-grid/grid/grid";
import Grid from "@/components/wijmo-grid/grid/grid.vue";
import { TitleListService } from "@/services";
import {
	LanguageSkillModel,
	VCSLevelModel,
	TitleListParams,
	DeleteContentParamsModel,
	PersistedData
} from "@/models/title-list-model";
import { getModule } from "vuex-module-decorators";
import { DateHelper } from "@/util/helper";
import { isUndefined } from "lodash";
import TitleListState from "@/modules/title-list";
import { VersionGroupModule } from "@/modules/versiongroup";
import { disabledDueToAccessLevel, getRouteAccessLevel, getSkillSelected, isAnyVersionSelected } from "@/util/func";
import { ContentCategoryEnum, ContentGroupNameEnum, ContentGroupEnum, CurriculumType, AccessLevel } from "@/util/enum";

@Component({
	components: {
		ChildNavigation,
		Spin,
		Select,
		Option: Select.Option,
		Icon,
		Grid,
		ActionItem
	}
})
export default class List extends Vue {
	public readonly allLanguageSkill = -1;
	@Prop() onCreateClick!: () => void;
	@Prop() onTitleClick!: (titleId: string) => void;
	@Ref() readonly gridCompRef!: GridRef;
	public titleListModule = getModule(TitleListState);
	public levelSelected: number = 0;
	public skillSelected: number[] = [];
	public loading: boolean = false;
	public isGridVisible: boolean = false;
	public isCurriculum: boolean = false;
	private versionGroupModule = VersionGroupModule; // For watch need to create local reference
	public searchTerm: string | null = this.titleListModule.persistedData.searchTerm;
	public possibleSearchTerm: string | null = this.titleListModule.persistedData.searchTerm;
	public currentPage = this.titleListModule.persistedData.currentPage;
	public currentSort: IWijmoSort = this.titleListModule.persistedData.currentSort;
	public locale = TitleListLocale;
	public accessLevel = AccessLevel;

	get languageSkills(): LanguageSkillModel[] {
		const noneSkill = this.titleListModule.languageSkills.slice(0, 1);
		return this.titleListModule.languageSkills.slice(1).concat(noneSkill);
	}

	get levels(): VCSLevelModel[] {
		if (this.versionGroupModule.selectedGsVersion === "-1") {
			return this.titleListModule.levels.filter((level) => level.curriculumType !== CurriculumType.GrapeSEED); // Filter out GS units if not selected.
		} else if (this.versionGroupModule.selectedLsVersion === "-1") {
			return this.titleListModule.levels.filter((level) => level.curriculumType !== CurriculumType.LittleSEED); // Filter out LS units if not selected.
		} else {
			return this.titleListModule.levels;
		}
	}

	get titleDefaultSort() {
		return {
			column: "title",
			ascending: true
		};
	}

	get ContentCategory() {
		return ContentCategoryEnum;
	}

	get ContentGroup() {
		return ContentGroupEnum;
	}

	get ContentGroupName() {
		return ContentGroupNameEnum;
	}

	get currentRouteAccessLevel() {
		return getRouteAccessLevel(this.$route);
	}

	get disabledDueToAccessLevel() {
		return disabledDueToAccessLevel(this.currentRouteAccessLevel);
	}

	@Watch("versionGroupModule.selectedGsVersion")
	@Watch("versionGroupModule.selectedLsVersion")
	isVersionUpdated() {
		if (isAnyVersionSelected()) {
			this.gridCompRef.reload();
		}
	}

	created() {
		this.levelSelected = this.titleListModule.listSelectedLevel;
		this.updateDefault();
	}

	updateDefault() {
		if (!isUndefined(this.languageSkills) && !isUndefined(this.levels)) {
			this.skillSelected = this.titleListModule.listSelectedSkills;
			this.isGridVisible = true;
		}
	}

	get pdfExportOptions() {
		return {
			excludeColumns: ["deleteTitle"],
			filename: this.localizedString(TitleListLocale.TitleListExportPdfTitle)
		};
	}

	errorCaptured(error: Error, component: any, info: string) {
		console.log("Create beautiful something went wrong. contact admin.");
	}

	setLoading(loadingValue: boolean) {
		this.loading = loadingValue;
	}

	get titleListLocale() {
		return TitleListLocale;
	}

	localizedString(id: string, params?: {}) {
		return fmtMsg(id, params);
	}

	formatData(data) {
		return data.data;
	}

	formatTagEditionDate(date) {
		return DateHelper.formatDate2Local(date);
	}

	get exportOptions() {
		return {
			filename: this.localizedString(TitleListLocale.TitleListExportExcelTitle),
			formatData: (data) => this.formatData(data)
		};
	}

	get titleListService() {
		return TitleListService.getTitleTextList;
	}

	getTitleListParams(): TitleListParams {
		return {
			gsVersionGroupId: VersionGroupModule.selectedGsVersion,
			lsVersionGroupId: VersionGroupModule.selectedLsVersion,
			levelId: this.levelSelected,
			languageSkillIds: this.skillSelected.includes(this.allLanguageSkill) ? [] : this.skillSelected,
			keyword: null,
			isCurriculum: true
		};
	}

	onTitleSearch(term: string) {
		if (this.possibleSearchTerm && this.possibleSearchTerm.length > 2) {
			this.searchTerm = term;
			Vue.nextTick(() => this.gridCompRef.reload()); // reload grid after search term set is complete
		}
	}

	searchValueChanged(term: string) {
		this.possibleSearchTerm = term;
	}

	resetSeachTerm() {
		this.searchTerm = null;
	}

	removeTitleText(id: any) {
		MessageHelper.Confirm(
			this.localizedString(this.titleListLocale.TitleListRemoveTitleConfirmMessage),
			() =>
				new Promise((resolve, reject) => {
					{
						const params: DeleteContentParamsModel = {
							contentId: id,
							GSVersionGroupId: VersionGroupModule.selectedGsVersion,
							LSVersionGroupId: VersionGroupModule.selectedLsVersion
						};

						TitleListService.deleteTextFromList(params)
							.then((data) => {
								MessageHelper.Message(
									NotificationType.Success,
									this.localizedString(this.titleListLocale.TitleListRemoveTitleSuccessMessage)
								);
								this.gridCompRef.reload();
								resolve(true);
							})
							.catch((e) => {
								reject();
							});
					}
				})
		);
	}

	onLevelChange(level: number) {
		this.titleListModule.setListSelectedLevel(level);
		this.gridCompRef.setParamWatcher(false);
		this.gridCompRef.setCurrentPage(1);
		this.searchTerm = null;
		Vue.nextTick(() => this.gridCompRef.reload()); // reload grid after search term reset is complete
	}

	onSkillChange(selectedValues) {
		this.skillSelected = getSkillSelected(selectedValues);
		this.titleListModule.setListSelectedSkills(this.skillSelected);
		this.searchTerm = null;
		this.gridCompRef.setParamWatcher(false);
		this.gridCompRef.setCurrentPage(1);
		Vue.nextTick(() => this.gridCompRef.reload()); // reload grid after search term reset is complete
	}

	public selectControlContainer(element: HTMLDivElement) {
		return element.parentNode;
	}

	public handlePaginationChange(currentPage: number) {
		this.currentPage = currentPage;
	}
	public handleSortChange(currentSort: IWijmoSort) {
		this.currentSort = currentSort;
	}

	get persistedCurrentPage() {
		return this.titleListModule.persistedData.currentPage;
	}
	get persistedCurrentSort() {
		return this.titleListModule.persistedData.currentSort;
	}

	handleTitleClick(titleId: string) {
		const { searchTerm, currentPage, currentSort } = this;
		const data: PersistedData = {
			searchTerm: searchTerm || "",
			currentPage,
			currentSort
		};
		this.titleListModule.changePersistedData(data);
		this.onTitleClick(titleId);
	}
}
